import { observer } from 'mobx-react-lite';
import type { MouseEvent } from 'react';

import AddToCartButton, {
  type AddToCartButtonSize,
} from '~/components/ProductCardV3/shared/AddToCartButton';
import CartCounter, {
  type CounterAction,
} from '~/components/ProductCardV3/shared/CartCounter';
import NotifyMeButton from '~/components/ProductCardV3/shared/NotifyMeButton';

export interface AddToCartSectionProps {
  /**
   * Current price to display
   */
  currentPrice: string;

  /**
   * Old price (shown when there's a discount)
   */
  oldPrice?: string;

  /**
   * Current number of items in cart
   */
  cartItemsCount: number;

  /**
   * Maximum allowed quantity
   */
  limit: number;

  /**
   * Size variant (m, s, xs)
   */
  size: AddToCartButtonSize;

  /**
   * Handler for adding/changing item count
   */
  onChangeCount: (newCount: number, action: CounterAction) => void;

  /**
   * Additional CSS class for the wrapper
   */
  className?: string;

  /**
   * Hide price in add button (show only icon)
   */
  hidePrice?: boolean;

  /**
   * Product ID (required for NotifyMe functionality)
   */
  productId: number;

  /**
   * Product SKU (required for NotifyMe functionality)
   */
  sku: string;

  /**
   * Whether product is out of stock
   */
  isOutOfStock?: boolean;

  /**
   * Product slug (for data-link attribute)
   */
  productSlug?: string;

  /**
   * Whether product has multiple variants
   */
  productHasManyVariants?: boolean;

  /**
   * Whether product is a bundle
   */
  isBundle?: boolean;

  /**
   * Handler to open SelectSKU modal
   */
  onOpenSelectSKUModal?: () => void;

  /**
   * Transparent state - card becomes transparent
   */
  transparent?: boolean;
}

const COUNTER_SIZE_MAP: Record<AddToCartButtonSize, 's' | 'm' | 'xs'> = {
  xs: 'xs',
  s: 's',
  m: 'm',
};

/**
 * AddToCartSection - Factory component for add to cart functionality
 * Shows NotifyMeButton when out of stock, AddToCartButton when cart is empty, CartCounter when item is added
 */
const AddToCartSection = observer(
  ({
    currentPrice,
    oldPrice,
    cartItemsCount,
    limit,
    size,
    onChangeCount,
    className,
    hidePrice = false,
    productId,
    sku,
    isOutOfStock = false,
    productSlug,
    productHasManyVariants = false,
    isBundle = false,
    onOpenSelectSKUModal,
    transparent = false,
  }: AddToCartSectionProps) => {
    const handleAddClick = (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();

      // If product has multiple variants and is not a bundle, open SelectSKU modal
      if (productHasManyVariants && !isBundle && onOpenSelectSKUModal) {
        onOpenSelectSKUModal();
      } else {
        // Otherwise, add item to cart directly
        onChangeCount(1, 'add');
      }
    };

    const handleCounterClick = (
      newCount: number,
      action: CounterAction,
      e: MouseEvent<HTMLButtonElement>,
    ) => {
      e.preventDefault();
      e.stopPropagation();
      onChangeCount(newCount, action);
    };

    // Show NotifyMeButton if product is out of stock
    if (isOutOfStock) {
      return (
        <NotifyMeButton
          size={size}
          productId={productId}
          sku={sku}
          isBundle={isBundle}
          className={className}
        />
      );
    }

    // Show CartCounter if item is in cart
    if (cartItemsCount > 0) {
      return (
        <CartCounter
          count={cartItemsCount}
          onClick={handleCounterClick}
          size={COUNTER_SIZE_MAP[size]}
          limit={limit}
          transparent={transparent}
          className={className}
        />
      );
    }

    // Show AddToCartButton by default
    return (
      <AddToCartButton
        currentPrice={currentPrice}
        oldPrice={oldPrice}
        size={size}
        onClick={handleAddClick}
        disabled={limit <= 0}
        hidePrice={hidePrice}
        className={className}
        productSlug={productSlug}
        transparent={transparent}
      />
    );
  },
);

export default AddToCartSection;
